import { useCallback, useEffect, useState } from 'react';
import { Title, useNotify, useGetList } from 'react-admin';
import { Box, Card, CardContent, Typography, Button, Alert, Stack, CircularProgress } from '@mui/material';
import { card, btn, typo } from '../styles/ds';
import { websiteRequest } from './websiteApi';
import ListingSlots from './ListingSlots';

/**
 * Best Sellers (WD-015): the admin chooses four public listings and their
 * order. A chosen listing that later stops being public stays here, marked,
 * so the admin can review or restore it; meanwhile the website shows the next
 * public listing of the same category in its slot (owner decision 2026-11-02).
 */
const SLOTS = 4;

const WebsiteBestSellersPage = () => {
  const notify = useNotify();
  const { data: listings = [] } = useGetList('website_products', {
    pagination: { page: 1, perPage: 1000 }, sort: { field: 'name_en', order: 'ASC' },
    filter: { publication_state: 'published' },
  });
  const [loaded, setLoaded] = useState(null);
  const [value, setValue] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    websiteRequest('/website_best_sellers')
      .then(r => { setLoaded(r.slots); setValue(r.slots.map(s => s.product_id)); setDirty(false); })
      .catch(err => notify(err.message, { type: 'error' }));
  }, [notify]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await websiteRequest('/website_best_sellers', 'PUT', {
        product_ids: value.filter(Boolean),
        previous: loaded.map(s => s.product_id),
      });
      notify('ה-Best Sellers נשמרו', { type: 'success' });
      setLoaded(r.slots); setValue(r.slots.map(s => s.product_id)); setDirty(false);
    } catch (err) { notify(err.message, { type: 'error' }); }
    finally { setSaving(false); }
  };

  if (!loaded) return <Box p={6} textAlign="center"><CircularProgress /></Box>;

  // Saved choices that aren't public right now stay selectable for review.
  const options = [...listings];
  for (const s of loaded) {
    if (!options.some(o => o.id === s.product_id)) options.push({ id: s.product_id, name_en: s.name_en, slug: s.slug, publication_state: s.retired_at ? undefined : 'unpublished' });
  }
  const ineligible = loaded.filter(s => !s.eligible);
  const warnings = Object.fromEntries(ineligible.map(s => [s.product_id, s.shown_name_en
    ? `לא מוצג כרגע באתר — במקומו מוצג אוטומטית: ${s.shown_name_en} (מאותה קטגוריה)`
    : 'לא מוצג כרגע באתר — ואין מוצר זמין מאותה קטגוריה, ולכן המקום ריק']));
  const filled = value.filter(Boolean).length;

  return (
    <Box className="crm-page" sx={{ p: { xs: 1, sm: 3 } }}>
      <Title title="Best Sellers באתר" />
      <Typography {...typo.pageTitle} mb={1}>Best Sellers באתר</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        ארבעת המוצרים שמוצגים בדף הבית ומסומנים כ-Best Seller, לפי הסדר שתבחרו. ניתן לבחור רק מוצרים שמפורסמים ומוצגים כרגע באתר.
      </Typography>
      {ineligible.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {ineligible.length === 1 ? 'מוצר אחד שנבחר אינו מוצג כרגע באתר' : `${ineligible.length} מוצרים שנבחרו אינם מוצגים כרגע באתר`}.
          הבחירה שלך נשמרה כפי שהיא, ובינתיים האתר מציג במקומו את המוצר הבא מאותה קטגוריה לפי סדר התצוגה. אפשר להחליף אותו או לפרסם אותו מחדש.
        </Alert>
      )}
      <Card sx={card.section}>
        <CardContent className="crm-card-content" sx={{ p: { xs: 2, sm: 3 } }}>
          <ListingSlots count={SLOTS} value={value} options={options} disabled={saving} warnings={warnings}
            onChange={v => { setValue(v); setDirty(true); }} />
          {filled < SLOTS && (
            <Typography sx={{ ...typo.meta, mt: 1.5 }}>נבחרו {filled} מתוך {SLOTS} — יש לבחור ארבעה מוצרים.</Typography>
          )}
          <Stack direction="row" spacing={1.5} mt={2}>
            <Button variant="contained" disabled={saving || !dirty} onClick={save} sx={btn.primary}>שמירה</Button>
            <Button variant="outlined" disabled={saving || !dirty} onClick={load} sx={btn.cancel}>ביטול שינויים</Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};
export default WebsiteBestSellersPage;
